import type { Task } from '@portfolio-pm/shared';
import { parseQuery } from './parser.ts';
import type { AtomQuery, Query } from './parser.ts';

export interface MatcherOptions {
  /** Reference time for "today/tomorrow/yesterday" date predicates. */
  now?: Date;
  /** Parent ids that currently have at least one child task. */
  childTaskIds?: Set<string>;
}

export interface CompiledQuery {
  match: (task: Task) => boolean;
}

export interface CompiledQueryError {
  error: string;
}

export type CompileResult = CompiledQuery | CompiledQueryError;

/** Local midnight at the start of the calendar day containing `d`. */
function startOfDayLocal(d: Date): number {
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
}

function addLocalDays(timestamp: number, days: number): number {
  const date = new Date(timestamp);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

function addLocalUnits(
  timestamp: number,
  amount: number,
  unit: 'day' | 'week' | 'month' | 'year',
): number {
  const date = new Date(timestamp);
  if (unit === 'day') date.setDate(date.getDate() + amount);
  if (unit === 'week') date.setDate(date.getDate() + amount * 7);
  if (unit === 'month') date.setMonth(date.getMonth() + amount);
  if (unit === 'year') date.setFullYear(date.getFullYear() + amount);
  return date.getTime();
}

/** Resolve a date keyword to the start of that local calendar day. */
function resolveDateKeyword(keyword: string, now: Date): number | undefined {
  const todayStart = startOfDayLocal(now);
  const normalized = keyword.toLowerCase();
  switch (normalized) {
    case 'today':
      return todayStart;
    case 'tomorrow':
      return addLocalDays(todayStart, 1);
    case 'yesterday':
      return addLocalDays(todayStart, -1);
    default:
      return resolveWeekday(normalized, todayStart);
  }
}

function resolveWeekday(value: string, todayStart: number): number | undefined {
  const weekdays = new Map([
    ['sunday', 0],
    ['monday', 1],
    ['tuesday', 2],
    ['wednesday', 3],
    ['thursday', 4],
    ['friday', 5],
    ['saturday', 6],
  ]);
  const target = weekdays.get(value);
  if (target === undefined) return undefined;
  const today = new Date(todayStart).getDay();
  const daysUntil = (target - today + 7) % 7;
  return addLocalDays(todayStart, daysUntil === 0 ? 7 : daysUntil);
}

function includesText(haystack: string | undefined, needle: string): boolean {
  return haystack?.toLowerCase().includes(needle.toLowerCase()) ?? false;
}

function dateRangeForValue(value: string | undefined, now: Date): [number, number] | undefined {
  if (value === undefined) return undefined;
  const normalized = value.toLowerCase();
  if (normalized === 'this-week') {
    const start = startOfDayLocal(now);
    return [start, addLocalDays(start, 7)];
  }
  if (normalized === 'next-week') {
    const start = addLocalDays(startOfDayLocal(now), 7);
    return [start, addLocalDays(start, 7)];
  }
  const relative = normalized.match(/^(\d+)\s*(day|days|week|weeks|month|months|year|years)$/);
  if (relative) {
    const amount = Number.parseInt(relative[1] ?? '0', 10);
    const unitText = relative[2] ?? 'day';
    const unit = unitText.startsWith('week')
      ? 'week'
      : unitText.startsWith('month')
        ? 'month'
        : unitText.startsWith('year')
          ? 'year'
          : 'day';
    const start = addLocalUnits(startOfDayLocal(now), amount, unit);
    return [start, addLocalDays(start, 1)];
  }

  const keyword = resolveDateKeyword(value, now);
  if (keyword !== undefined) return [keyword, addLocalDays(keyword, 1)];

  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    const start = startOfDayLocal(new Date(parsed));
    return [start, addLocalDays(start, 1)];
  }

  return undefined;
}

function matchDateAtom(
  value: string | undefined,
  timestamp: number | undefined,
  now: Date,
): boolean {
  if (timestamp === undefined) return false;
  const range = dateRangeForValue(value, now);
  return range !== undefined && timestamp >= range[0] && timestamp < range[1];
}

function matchBeforeAtom(
  value: string | undefined,
  timestamp: number | undefined,
  now: Date,
): boolean {
  if (value === undefined || timestamp === undefined) return false;
  const range = dateRangeForValue(value, now);
  return range !== undefined && timestamp < range[0];
}

function matchAfterAtom(
  value: string | undefined,
  timestamp: number | undefined,
  now: Date,
): boolean {
  if (value === undefined || timestamp === undefined) return false;
  const range = dateRangeForValue(value, now);
  return range !== undefined && timestamp >= range[1];
}

function matchWithinAtom(
  value: string | undefined,
  timestamp: number | undefined,
  now: Date,
): boolean {
  if (value === undefined || timestamp === undefined) return false;
  const range = periodRangeForValue(value, now);
  return range !== undefined && timestamp >= range[0] && timestamp < range[1];
}

function periodRangeForValue(value: string, now: Date): [number, number] | undefined {
  const match = value
    .trim()
    .toLowerCase()
    .match(/^(\d+)\s*(day|days|week|weeks|month|months|year|years)(?:\s+of\s+(.+))?$/);
  if (!match) return undefined;
  const amount = Number.parseInt(match[1] ?? '0', 10);
  const unitText = match[2] ?? 'day';
  const anchorText = match[3]?.trim() ?? 'today';
  if (amount <= 0) return undefined;

  const anchorRange = dateRangeForValue(anchorText, now);
  if (!anchorRange) return undefined;
  const unit = unitText.startsWith('week')
    ? 'week'
    : unitText.startsWith('month')
      ? 'month'
      : unitText.startsWith('year')
        ? 'year'
        : 'day';
  return [anchorRange[0], addLocalUnits(anchorRange[0], amount, unit)];
}

function matchBoolean(value: string | undefined, actual: boolean): boolean {
  if (value === undefined) return actual;
  const normalized = value.toLowerCase();
  if (['true', 'yes', '1'].includes(normalized)) return actual;
  if (['false', 'no', '0'].includes(normalized)) return !actual;
  return false;
}

function matchNumberAtom(value: string | undefined, actual: number | undefined): boolean {
  if (value === undefined) return false;
  if (value === '*') return actual !== undefined;
  if (actual === undefined) return false;
  const match = value.trim().match(/^(<=|>=|<|>|=)?\s*(\d+)$/);
  if (!match) return false;
  const operator = match[1] ?? '=';
  const expected = Number.parseInt(match[2] ?? '0', 10);
  if (operator === '<') return actual < expected;
  if (operator === '<=') return actual <= expected;
  if (operator === '>') return actual > expected;
  if (operator === '>=') return actual >= expected;
  return actual === expected;
}

function matchAtom(atom: AtomQuery, task: Task, now: Date, childTaskIds: Set<string>): boolean {
  const key = normalizeKey(atom.key);
  switch (atom.key) {
    case 'raw':
      return atom.value !== undefined && matches(atom.value, task, { now, childTaskIds });
  }

  switch (key) {
    case 'all':
      return true;
    case 'priority':
      if (atom.value === '*') return task.priority !== undefined;
      if (atom.value === 'none') return task.priority === 0;
      return atom.value !== undefined && task.priority === Number(atom.value);
    case 'tag':
      if (atom.value === '*') return task.tags.length > 0;
      return atom.value !== undefined && task.tags.includes(atom.value);
    case 'tagcontains': {
      const value = atom.value;
      return value !== undefined && task.tags.some((tag) => includesText(tag, value));
    }
    case 'project':
      if (atom.value === '*') return Boolean(task.projectId);
      return atom.value !== undefined && task.projectId === atom.value;
    case 'listcontains':
      return atom.value !== undefined && includesText(task.projectId, atom.value);
    case 'owner':
      if (atom.value === '*') return task.owner !== undefined;
      return atom.value !== undefined && task.owner === atom.value;
    case 'url':
      return atom.value === undefined ? task.url !== undefined : includesText(task.url, atom.value);
    case 'hasurl':
      return matchBoolean(atom.value, task.url !== undefined);
    case 'hasnotes':
      return matchBoolean(atom.value, Boolean(task.notes?.trim()));
    case 'location':
      if (atom.value === '*') return task.location !== undefined;
      return atom.value !== undefined && includesText(task.location, atom.value);
    case 'estimate':
    case 'timeestimate':
      return matchNumberAtom(atom.value, task.estimateMinutes);
    case 'hastimeestimate':
      return matchBoolean(atom.value, task.estimateMinutes !== undefined);
    case 'notimeestimate':
      return matchBoolean(atom.value, task.estimateMinutes === undefined);
    case 'postponed':
      if (atom.value === '*') return task.postponed !== undefined;
      return matchNumberAtom(atom.value, task.postponed ?? 0);
    case 'source':
      if (atom.value === '*') return task.source !== undefined;
      return atom.value !== undefined && includesText(task.source, atom.value);
    case 'favorite':
      return matchBoolean(atom.value, task.favorite === true);
    case 'repeat':
    case 'recurrence':
      return matchBoolean(atom.value, task.recurrence !== undefined);
    case 'trashed':
      return matchBoolean(atom.value, task.trashedAt !== undefined);
    case 'kr':
      return atom.value !== undefined && task.krId === atom.value;
    case 'workflow':
      return atom.value !== undefined && task.workflowState === atom.value;
    case 'parent':
      return atom.value !== undefined && task.parentTaskId === atom.value;
    case 'child':
      return task.parentTaskId !== undefined;
    case 'issubtask':
      return matchBoolean(atom.value, task.parentTaskId !== undefined);
    case 'root':
      return task.parentTaskId === undefined;
    case 'haschildren':
    case 'hassubtasks':
      return childTaskIds.has(task.id);
    case 'text':
    case 'title': {
      const value = atom.value;
      return (
        value !== undefined &&
        (includesText(task.title, value) ||
          includesText(task.notes, value) ||
          task.tags.some((tag) => includesText(tag, value)))
      );
    }
    case 'completed':
      return matchBoolean(atom.value, task.completedAt !== undefined);
    case 'incomplete':
      return task.completedAt === undefined;
    case 'status': {
      const value = atom.value?.toLowerCase();
      if (value === 'open' || value === 'incomplete') return task.completedAt === undefined;
      if (value === 'completed' || value === 'complete') return task.completedAt !== undefined;
      if (value === 'trashed' || value === 'trash') return task.trashedAt !== undefined;
      return false;
    }
    case 'energy':
      return atom.value !== undefined && task.energy === atom.value;
    case 'daypart':
      return atom.value !== undefined && task.daypart === atom.value;
    case 'duebefore': {
      return matchBeforeAtom(atom.value, task.dueAt, now);
    }
    case 'dueafter': {
      return matchAfterAtom(atom.value, task.dueAt, now);
    }
    case 'dueon': {
      return matchDateAtom(atom.value, task.dueAt, now);
    }
    case 'duewithin': {
      return matchWithinAtom(atom.value, task.dueAt, now);
    }
    case 'due': {
      if (atom.value === 'never' || atom.value === 'none') return task.dueAt === undefined;
      return matchDateAtom(atom.value, task.dueAt, now);
    }
    case 'scheduledbefore': {
      return matchBeforeAtom(atom.value, task.scheduledAt, now);
    }
    case 'scheduledafter': {
      return matchAfterAtom(atom.value, task.scheduledAt, now);
    }
    case 'scheduledon': {
      return matchDateAtom(atom.value, task.scheduledAt, now);
    }
    case 'scheduledwithin': {
      return matchWithinAtom(atom.value, task.scheduledAt, now);
    }
    case 'scheduled': {
      if (atom.value === 'never' || atom.value === 'none') return task.scheduledAt === undefined;
      return matchDateAtom(atom.value, task.scheduledAt, now);
    }
    case 'createdbefore':
    case 'addedbefore':
      return matchBeforeAtom(atom.value, task.createdAt, now);
    case 'createdafter':
    case 'addedafter':
      return matchAfterAtom(atom.value, task.createdAt, now);
    case 'createdon':
    case 'addedon':
    case 'created':
    case 'added':
      return matchDateAtom(atom.value, task.createdAt, now);
    case 'createdwithin':
    case 'addedwithin':
      return matchWithinAtom(atom.value, task.createdAt, now);
    case 'updatedbefore':
      return matchBeforeAtom(atom.value, task.updatedAt, now);
    case 'updatedafter':
      return matchAfterAtom(atom.value, task.updatedAt, now);
    case 'updatedon':
    case 'updated':
      return matchDateAtom(atom.value, task.updatedAt, now);
    case 'updatedwithin':
      return matchWithinAtom(atom.value, task.updatedAt, now);
    case 'completedbefore':
      return matchBeforeAtom(atom.value, task.completedAt, now);
    case 'completedafter':
      return matchAfterAtom(atom.value, task.completedAt, now);
    case 'completedon':
      return matchDateAtom(atom.value, task.completedAt, now);
    case 'completedwithin':
      return matchWithinAtom(atom.value, task.completedAt, now);
    case 'overdue': {
      const todayStart = startOfDayLocal(now);
      return task.completedAt === undefined && task.dueAt !== undefined && task.dueAt < todayStart;
    }
    default:
      return false;
  }
}

function normalizeKey(key: string): string {
  switch (key.toLowerCase()) {
    case 'duebefore':
    case 'beforedue':
      return 'duebefore';
    case 'dueafter':
    case 'afterdue':
      return 'dueafter';
    case 'dueon':
      return 'dueon';
    case 'duewithin':
      return 'duewithin';
    case 'scheduledbefore':
    case 'startbefore':
      return 'scheduledbefore';
    case 'scheduledafter':
    case 'startafter':
      return 'scheduledafter';
    case 'scheduledon':
    case 'start':
      return 'scheduledon';
    case 'scheduledwithin':
    case 'startwithin':
      return 'scheduledwithin';
    case 'list':
      return 'project';
    case 'tagcontains':
      return 'tagcontains';
    case 'listcontains':
      return 'listcontains';
    case 'tags':
      return 'tag';
    case 'name':
      return 'title';
    case 'hasurl':
    case 'has_url':
      return 'hasurl';
    case 'hasnotes':
    case 'has_notes':
      return 'hasnotes';
    case 'hassubtasks':
    case 'has_subtasks':
      return 'hassubtasks';
    case 'issubtask':
    case 'is_subtask':
      return 'issubtask';
    case 'favourite':
      return 'favorite';
    case 'timeestimate':
    case 'time_estimate':
      return 'timeestimate';
    case 'hastimeestimate':
    case 'has_time_estimate':
      return 'hastimeestimate';
    case 'notimeestimate':
    case 'no_time_estimate':
      return 'notimeestimate';
    default:
      return key.toLowerCase();
  }
}

function matchQuery(query: Query, task: Task, now: Date, childTaskIds: Set<string>): boolean {
  switch (query.type) {
    case 'atom':
      return matchAtom(query, task, now, childTaskIds);
    case 'and':
      return (
        matchQuery(query.left, task, now, childTaskIds) &&
        matchQuery(query.right, task, now, childTaskIds)
      );
    case 'or':
      return (
        matchQuery(query.left, task, now, childTaskIds) ||
        matchQuery(query.right, task, now, childTaskIds)
      );
    case 'not':
      return !matchQuery(query.child, task, now, childTaskIds);
  }
}

/** Parse + match in one shot. Returns false on a parse error. */
export function matches(input: string, task: Task, options: MatcherOptions = {}): boolean {
  const parsed = parseQuery(input);
  if ('error' in parsed) return false;
  return matchQuery(parsed, task, options.now ?? new Date(), options.childTaskIds ?? new Set());
}

/** Parse the query once and return a reusable predicate. */
export function compileQuery(input: string, options: MatcherOptions = {}): CompileResult {
  const parsed = parseQuery(input);
  if ('error' in parsed) return { error: parsed.error };
  const now = options.now ?? new Date();
  const childTaskIds = options.childTaskIds ?? new Set<string>();
  return {
    match: (task: Task) => matchQuery(parsed, task, now, childTaskIds),
  };
}
